import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  ArrowLeft,
  Plus,
  Download,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  DollarSign,
  Calendar,
  CreditCard,
  Landmark,
  CreditCard as StripeIcon
} from 'lucide-react';
import { toast } from 'react-toastify';

interface BankDetails {
  accountName: string;
  accountNumber: string;
  bankName: string;
  routingNumber?: string;
}

interface ProcessedBy {
  _id: string;
  firstName: string;
  lastName: string;
}

interface WithdrawalRequest {
  _id: string;
  amount: number;
  currency: string;
  status: string;
  paymentMethod: {
    type: string;
    details: any;
  };
  referenceNumber: string;
  processedBy?: ProcessedBy;
  processedAt?: string;
  completedAt?: string;
  failureReason?: string;
  adminNotes?: string;
  userNotes?: string;
  createdAt: string;
}

// Get the correct API base URL based on environment
const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (hostname.includes('webcontainer.io') || hostname.includes('webcontainer-api.io')) {
      const baseUrl = hostname.replace('--5173--', '--5002--');
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};


const WithdrawalRequests: React.FC = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewRequestForm, setShowNewRequestForm] = useState(false);
  const [error, setError] = useState('');
  const [availableBalance, setAvailableBalance] = useState(0);
  const [showPaymentMethodForm, setShowPaymentMethodForm] = useState(false);
  const [paymentMethodType, setPaymentMethodType] = useState('bank');

  const [newRequest, setNewRequest] = useState({
    amount: 0,
    currency: 'USD',
    userNotes: ''
  });

  const [bankDetails, setBankDetails] = useState({
    accountName: '',
    accountNumber: '',
    bankName: '',
    routingNumber: ''
  });

  const [stripeDetails, setStripeDetails] = useState({
    accountId: '',
    email: '',
    country: 'US'
  });

  const [paystackDetails, setPaystackDetails] = useState({
    email: '',
    accountNumber: '',
    bankCode: '',
    bankName: ''
  });

  const API_BASE_URL = getApiBaseUrl();

  useEffect(() => {
    fetchWithdrawals();
    fetchAvailableBalance();
  }, []);

  const fetchWithdrawals = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/withdrawals`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch withdrawals');
      }

      const data = await response.json();
      setWithdrawals(data.withdrawals || []);
      setError('');
    } catch (error) {
      setError('Failed to load withdrawal requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableBalance = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/earnings/summary`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch available balance');
      }

      const data = await response.json();
      setAvailableBalance(data.available?.USD?.amount || 0);
    } catch (error) {
      // Don't set error here as it's not critical
    }
  };

  const handleSubmitRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newRequest.amount <= 0) {
      setError('Withdrawal amount must be greater than zero');
      return;
    }

    if (newRequest.amount > availableBalance) {
      setError('Withdrawal amount cannot exceed available balance');
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      
      let paymentMethod;
      
      if (paymentMethodType === 'bank') {
        if (!bankDetails.accountName || !bankDetails.accountNumber || !bankDetails.bankName) {
          setError('Please fill in all required bank details');
          return;
        }
        
        paymentMethod = {
          type: 'bank',
          details: bankDetails
        };
      } else if (paymentMethodType === 'stripe') {
        if (!stripeDetails.email) {
          setError('Please fill in all required Stripe details');
          return;
        }
        
        paymentMethod = {
          type: 'stripe',
          details: stripeDetails
        };
      } else if (paymentMethodType === 'paystack') {
        if (!paystackDetails.email || !paystackDetails.accountNumber || !paystackDetails.bankName) {
          setError('Please fill in all required Paystack details');
          return;
        }
        
        paymentMethod = {
          type: 'paystack',
          details: paystackDetails
        };
      } else {
        setError('Please select a payment method');
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/withdrawals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: newRequest.amount,
          currency: newRequest.currency,
          paymentMethod,
          userNotes: newRequest.userNotes
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create withdrawal request');
      }

      const data = await response.json();
      
      // Reset form
      setNewRequest({
        amount: 0,
        currency: 'USD',
        userNotes: ''
      });
      setBankDetails({
        accountName: '',
        accountNumber: '',
        bankName: '',
        routingNumber: ''
      });
      setStripeDetails({
        accountId: '',
        email: '',
        country: 'US'
      });
      setPaystackDetails({
        email: '',
        accountNumber: '',
        bankCode: '',
        bankName: ''
      });
      
      setShowNewRequestForm(false);
      setShowPaymentMethodForm(false);
      
      // Refresh data
      await fetchWithdrawals();
      await fetchAvailableBalance();
      
      toast.success('Withdrawal request submitted successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to create withdrawal request');
      toast.error(err.message || 'Failed to create withdrawal request');
    }
  };

  const handleCancelWithdrawal = async (id: string) => {
    if (!confirm('Are you sure you want to cancel this withdrawal request?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/withdrawals/${id}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to cancel withdrawal request');
      }

      // Refresh data
      await fetchWithdrawals();
      await fetchAvailableBalance();
      
      toast.success('Withdrawal request cancelled successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to cancel withdrawal request');
      toast.error(err.message || 'Failed to cancel withdrawal request');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'processing': return 'text-blue-600 bg-blue-100';
      case 'completed': return 'text-green-600 bg-green-100';
      case 'failed': return 'text-red-600 bg-red-100';
      case 'cancelled': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'processing': return <Download className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'failed': return <XCircle className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(amount);
  };

  const getRelativeTime = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div
          className={`h-16 ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'
          } rounded-lg animate-pulse`}
        ></div>
        <div className="space-y-4">
          {[...Array(3)].map((_, index) => (
            <div key={index} className={`${
              theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } p-6 rounded-lg shadow-sm border animate-pulse`}>
              <div
                className={`h-4 ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                } rounded w-3/4 mb-4`}
              ></div>
              <div
                className={`h-3 ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                } rounded w-full mb-2`}
              ></div>
              <div
                className={`h-3 ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                } rounded w-2/3`}
              ></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/dashboard/earnings"
            className={`p-2 ${
              theme === 'dark'
                ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            } rounded-lg transition-colors duration-200`}
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1
              className={`text-2xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}
            >
              Withdrawal Requests
            </h1>
            <p
              className={`${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              } mt-1`}
            >
              Manage your withdrawal requests
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowNewRequestForm(true)}
          className={`flex items-center px-4 py-2 ${
            theme === 'dark'
              ? 'bg-green-600 hover:bg-green-700'
              : 'bg-green-600 hover:bg-green-700'
          } text-white rounded-lg transition-colors duration-200`}
        >
          <Plus className="w-4 h-4 mr-2" />
          New Request
        </button>
      </div>

      {error && (
        <div className={`${
          theme === 'dark' ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'
        } border rounded-lg p-4`}>
          <div className="flex items-center">
            <AlertCircle className={`w-5 h-5 ${
              theme === 'dark' ? 'text-red-400' : 'text-red-500'
            } mr-2`} />
            <span className={`${
              theme === 'dark' ? 'text-red-300' : 'text-red-700'
            } text-sm`}>{error}</span>
          </div>
        </div>
      )}

      {/* Available Balance */}
      <div className={`bg-gradient-to-r ${
        theme === 'dark' ? 'from-green-800 to-green-900' : 'from-green-500 to-green-600'
      } p-6 rounded-lg text-white`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold mb-2">Available Balance</h2>
            <p className="text-3xl font-bold">{formatCurrency(availableBalance)}</p>
            <p className={`${
              theme === 'dark' ? 'text-green-200' : 'text-green-100'
            } mt-1`}>Ready for withdrawal</p>
          </div>
          <div className={`w-16 h-16 ${
            theme === 'dark' ? 'bg-white/10' : 'bg-white/20'
          } rounded-full flex items-center justify-center`}>
            <DollarSign className="w-8 h-8" />
          </div>
        </div>
      </div>

      {/* New Request Form */}
      {showNewRequestForm && (
        <div className={`${
          theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        } rounded-lg shadow-sm border`}>
          <div className={`p-6 border-b ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <h2 className={`text-lg font-semibold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>New Withdrawal Request</h2>
          </div>
          
          <form onSubmit={handleSubmitRequest} className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm font-medium ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                } mb-2`}>
                  Withdrawal Amount *
                </label>
                <div className="relative">
                  <DollarSign className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                    theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                  } w-4 h-4`} />
                  <input
                    type="number"
                    step="0.01"
                    min="1"
                    max={availableBalance}
                    required
                    value={newRequest.amount}
                    onChange={(e) => setNewRequest(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                    className={`pl-10 pr-4 py-2 w-full border ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                    placeholder="0.00"
                  />
                </div>
                <p className={`text-xs ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                } mt-1`}>
                  Maximum: {formatCurrency(availableBalance)}
                </p>
              </div>

              <div>
                <label className={`block text-sm font-medium ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                } mb-2`}>
                  Currency *
                </label>
                <select
                  value={newRequest.currency}
                  onChange={(e) => setNewRequest(prev => ({ ...prev, currency: e.target.value }))}
                  className={`w-full px-3 py-2 border ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  } rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                >
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="GBP">GBP (£)</option>
                </select>
              </div>
            </div>

            {/* Payment Method Selection */}
            <div>
              <label className={`block text-sm font-medium ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              } mb-2`}>
                Payment Method *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setPaymentMethodType('bank');
                    setShowPaymentMethodForm(true);
                  }}
                  className={`p-4 border-2 rounded-lg text-left transition-all duration-200 ${
                    paymentMethodType === 'bank' && showPaymentMethodForm
                      ? theme === 'dark'
                        ? 'border-green-500 bg-green-900/20 text-green-400'
                        : 'border-green-500 bg-green-50 text-green-700'
                      : theme === 'dark'
                      ? 'border-gray-700 hover:border-gray-600'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <Landmark className="w-6 h-6 mb-2" />
                  <div className={`font-medium ${
                    theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                  }`}>Bank Transfer</div>
                  <div className={`text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>Direct to your bank account</div>
                </button>
                
                <button
                  type="button"
                  onClick={() => {
                    setPaymentMethodType('stripe');
                    setShowPaymentMethodForm(true);
                  }}
                  className={`p-4 border-2 rounded-lg text-left transition-all duration-200 ${
                    paymentMethodType === 'stripe' && showPaymentMethodForm
                      ? theme === 'dark'
                        ? 'border-blue-500 bg-blue-900/20 text-blue-400'
                        : 'border-blue-500 bg-blue-50 text-blue-700'
                      : theme === 'dark'
                      ? 'border-gray-700 hover:border-gray-600'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <StripeIcon className="w-6 h-6 mb-2" />
                  <div className={`font-medium ${
                    theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                  }`}>Stripe</div>
                  <div className={`text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>Connect with Stripe account</div>
                </button>
                
                <button
                  type="button"
                  onClick={() => {
                    setPaymentMethodType('paystack');
                    setShowPaymentMethodForm(true);
                  }}
                  className={`p-4 border-2 rounded-lg text-left transition-all duration-200 ${
                    paymentMethodType === 'paystack' && showPaymentMethodForm
                      ? theme === 'dark'
                        ? 'border-purple-500 bg-purple-900/20 text-purple-400'
                        : 'border-purple-500 bg-purple-50 text-purple-700'
                      : theme === 'dark'
                      ? 'border-gray-700 hover:border-gray-600'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <CreditCard className="w-6 h-6 mb-2" />
                  <div className={`font-medium ${
                    theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                  }`}>Paystack</div>
                  <div className={`text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>Nigerian bank accounts</div>
                </button>
              </div>
            </div>

            {/* Bank Details Form */}
            {showPaymentMethodForm && paymentMethodType === 'bank' && (
              <div className="space-y-4">
                <h3 className={`text-lg font-semibold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>Bank Details</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    } mb-2`}>
                      Account Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={bankDetails.accountName}
                      onChange={(e) => setBankDetails(prev => ({
                        ...prev,
                        accountName: e.target.value
                      }))}
                      className={`w-full px-3 py-2 border ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                      placeholder="Full name on account"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    } mb-2`}>
                      Account Number *
                    </label>
                    <input
                      type="text"
                      required
                      value={bankDetails.accountNumber}
                      onChange={(e) => setBankDetails(prev => ({
                        ...prev,
                        accountNumber: e.target.value
                      }))}
                      className={`w-full px-3 py-2 border ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                      placeholder="Account number"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    } mb-2`}>
                      Bank Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={bankDetails.bankName}
                      onChange={(e) => setBankDetails(prev => ({
                        ...prev,
                        bankName: e.target.value
                      }))}
                      className={`w-full px-3 py-2 border ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                      placeholder="Bank name"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    } mb-2`}>
                      Routing Number
                    </label>
                    <input
                      type="text"
                      value={bankDetails.routingNumber}
                      onChange={(e) => setBankDetails(prev => ({
                        ...prev,
                        routingNumber: e.target.value
                      }))}
                      className={`w-full px-3 py-2 border ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                      placeholder="Routing number (if applicable)"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Stripe Details Form */}
            {showPaymentMethodForm && paymentMethodType === 'stripe' && (
              <div className="space-y-4">
                <h3 className={`text-lg font-semibold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>Stripe Account Details</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    } mb-2`}>
                      Stripe Account ID
                    </label>
                    <input
                      type="text"
                      value={stripeDetails.accountId}
                      onChange={(e) => setStripeDetails(prev => ({
                        ...prev,
                        accountId: e.target.value
                      }))}
                      className={`w-full px-3 py-2 border ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      placeholder="acct_..."
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    } mb-2`}>
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={stripeDetails.email}
                      onChange={(e) => setStripeDetails(prev => ({
                        ...prev,
                        email: e.target.value
                      }))}
                      className={`w-full px-3 py-2 border ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      placeholder="email@example.com"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    } mb-2`}>
                      Country *
                    </label>
                    <select
                      required
                      value={stripeDetails.country}
                      onChange={(e) => setStripeDetails(prev => ({
                        ...prev,
                        country: e.target.value
                      }))}
                      className={`w-full px-3 py-2 border ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    >
                      <option value="US">United States</option>
                      <option value="GB">United Kingdom</option>
                      <option value="CA">Canada</option>
                      <option value="AU">Australia</option>
                      <option value="DE">Germany</option>
                      <option value="FR">France</option>
                      <option value="GH">Ghana</option>
                      <option value="NG">Nigeria</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Paystack Details Form */}
            {showPaymentMethodForm && paymentMethodType === 'paystack' && (
              <div className="space-y-4">
                <h3 className={`text-lg font-semibold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>Paystack Account Details</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    } mb-2`}>
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={paystackDetails.email}
                      onChange={(e) => setPaystackDetails(prev => ({
                        ...prev,
                        email: e.target.value
                      }))}
                      className={`w-full px-3 py-2 border ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                      placeholder="email@example.com"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    } mb-2`}>
                      Account Number *
                    </label>
                    <input
                      type="text"
                      required
                      value={paystackDetails.accountNumber}
                      onChange={(e) => setPaystackDetails(prev => ({
                        ...prev,
                        accountNumber: e.target.value
                      }))}
                      className={`w-full px-3 py-2 border ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                      placeholder="0123456789"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    } mb-2`}>
                      Bank Code *
                    </label>
                    <input
                      type="text"
                      required
                      value={paystackDetails.bankCode}
                      onChange={(e) => setPaystackDetails(prev => ({
                        ...prev,
                        bankCode: e.target.value
                      }))}
                      className={`w-full px-3 py-2 border ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                      placeholder="057"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    } mb-2`}>
                      Bank Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={paystackDetails.bankName}
                      onChange={(e) => setPaystackDetails(prev => ({
                        ...prev,
                        bankName: e.target.value
                      }))}
                      className={`w-full px-3 py-2 border ${
                        theme === 'dark'
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                      } rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                      placeholder="Zenith Bank"
                    />
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className={`block text-sm font-medium ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              } mb-2`}>
                Notes (Optional)
              </label>
              <textarea
                rows={3}
                value={newRequest.userNotes}
                onChange={(e) => setNewRequest(prev => ({ ...prev, userNotes: e.target.value }))}
                className={`w-full px-3 py-2 border ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                } rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent`}
                placeholder="Any additional notes or instructions"
              />
            </div>

            <div className={`flex items-center justify-end space-x-4 pt-6 border-t ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <button
                type="button"
                onClick={() => {
                  setShowNewRequestForm(false);
                  setShowPaymentMethodForm(false);
                }}
                className={`px-4 py-2 ${
                  theme === 'dark'
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } rounded-lg transition-colors duration-200`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!showPaymentMethodForm || newRequest.amount <= 0}
                className={`px-6 py-2 ${
                  theme === 'dark' ? 'bg-green-600 hover:bg-green-700' : 'bg-green-600 hover:bg-green-700'
                } text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200`}
              >
                Submit Request
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Withdrawal Requests List */}
      <div className={`${
        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      } rounded-lg shadow-sm border`}>
        <div className={`p-6 border-b ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <h2 className={`text-lg font-semibold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>Withdrawal History</h2>
        </div>

        <div className="p-6">
          {withdrawals.length === 0 ? (
            <div className="text-center py-12">
              <Download className={`w-16 h-16 ${
                theme === 'dark' ? 'text-gray-700' : 'text-gray-300'
              } mx-auto mb-4`} />
              <h3 className={`text-lg font-medium ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              } mb-2`}>No withdrawal requests</h3>
              <p className={`${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              } mb-6`}>You haven't made any withdrawal requests yet.</p>
              <button
                onClick={() => setShowNewRequestForm(true)}
                className={`inline-flex items-center px-4 py-2 ${
                  theme === 'dark' ? 'bg-green-600 hover:bg-green-700' : 'bg-green-600 hover:bg-green-700'
                } text-white rounded-lg transition-colors duration-200`}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Request
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {withdrawals.map((withdrawal) => (
                <div
                  key={withdrawal._id}
                  className={`border ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                  } rounded-lg p-6 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors duration-200`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className={`flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(withdrawal.status)}`}>
                          {getStatusIcon(withdrawal.status)}
                          <span className="ml-2 capitalize">{withdrawal.status}</span>
                        </div>
                        <span className={`text-sm ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          Ref: {withdrawal.referenceNumber}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className={`${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                          }`}>Amount:</span>
                          <p className={`font-semibold ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>
                            {formatCurrency(withdrawal.amount, withdrawal.currency)}
                          </p>
                        </div>
                        
                        <div>
                          <span className={`${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                          }`}>Method:</span>
                          <p className={`font-medium ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>
                            {withdrawal.paymentMethod.type.charAt(0).toUpperCase() + withdrawal.paymentMethod.type.slice(1)}
                          </p>
                          {withdrawal.paymentMethod.type === 'bank' && (
                            <p className={`text-xs ${
                              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                              {withdrawal.paymentMethod.details.bankName}
                            </p>
                          )}
                        </div>
                        
                        <div>
                          <span className={`${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                          }`}>Requested:</span>
                          <p className={`font-medium ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>
                            {getRelativeTime(withdrawal.createdAt)}
                          </p>
                          {withdrawal.completedAt && (
                            <p className={`text-xs ${
                              theme === 'dark' ? 'text-green-400' : 'text-green-600'
                            }`}>
                              Completed {getRelativeTime(withdrawal.completedAt)}
                            </p>
                          )}
                        </div>
                      </div>

                      {withdrawal.adminNotes && (
                        <div className={`mt-4 p-3 ${
                          theme === 'dark' ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'
                        } border rounded-lg`}>
                          <p className={`text-sm ${
                            theme === 'dark' ? 'text-blue-300' : 'text-blue-800'
                          }`}>
                            <strong>Admin Note:</strong> {withdrawal.adminNotes}
                          </p>
                        </div>
                      )}

                      {withdrawal.failureReason && (
                        <div className={`mt-4 p-3 ${
                          theme === 'dark' ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'
                        } border rounded-lg`}>
                          <p className={`text-sm ${
                            theme === 'dark' ? 'text-red-300' : 'text-red-800'
                          }`}>
                            <strong>Failure Reason:</strong> {withdrawal.failureReason}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <div className="ml-4">
                      <div className={`text-xl font-bold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        {formatCurrency(withdrawal.amount, withdrawal.currency)}
                      </div>
                      
                      {withdrawal.status === 'pending' && (
                        <button
                          onClick={() => handleCancelWithdrawal(withdrawal._id)}
                          className={`mt-2 text-sm ${
                            theme === 'dark' ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'
                          }`}
                        >
                          Cancel Request
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WithdrawalRequests;